import React, {
  Fragment,
  useState,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { Element } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { Route } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import isEmpty from 'lodash/isEmpty';
import { FiArrowLeft } from 'react-icons/fi';
import Sticky from 'react-stickynode';
import moment from 'moment';
import { GlobalContext, getMarinaTextId } from 'context/GlobalProvider';
import { AuthContext } from 'context/AuthProvider';
import { LayoutContext } from 'context/LayoutProvider';
import { PopeyeContext } from 'context/PopeyeProvider';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import Heading from 'components/UI/Heading/Heading';
import Text from 'components/UI/Text/Text';
import Row from 'components/UI/Antd/Grid/Row';
import Col from 'components/UI/Antd/Grid/Col';
import Modal from 'components/UI/Antd/Modal/Modal';
import Button from 'components/UI/Antd/Button/Button';
import Drawer from 'components/UI/Antd/Drawer/Drawer';
import ImageWithFallback from 'components/UI/ImageWithFallback/ImageWithFallback';
import Container from 'components/UI/Container/Container';
import Loader from 'components/Loader/Loader';
import CardCarousel from 'components/CardCarousel/CardCarousel';
import VesselSelect from 'components/VesselSelect/VesselSelect';
import useWindowSize from 'library/hooks/useWindowSize';
import Description from './Description/Description';
import Amenities from './Amenities/Amenities';
import Location from './Location/Location';
import PromotionsList from './PromotionsList/PromotionsList';
import Reservation from './Reservation/Reservation';
import BottomReservation from './Reservation/BottomReservation';
import MessagingPage from './MessagingPage/MessagingPage';
import Messages from './MessagingPage/Messages';
import TopBar from './TopBar/TopBar';
import SinglePageWrapper, {
  PostImage,
  MobileMainDescription,
  PromotionsWrapperMobile,
  MessagingHeader,
  BackButtonTitle
} from './SinglePageView.style';
import PostImageGallery from './ImageGallery/ImageGallery';
import { NOT_FOUND_PAGE, MESSAGES_PAGE, MARINA_PAGE } from 'settings/constant';
import { ProtectedRoute } from 'router';
import { getStateFromUrl, setStateToUrl } from 'library/helpers/url_handler';
import { useSessionStorage } from 'library/hooks/useSessionStorage';
import useAmplitude from 'library/hooks/useAmplitude';
import MarinaReservation from "../Reservation/MarinaReservation";
import {Button as CustomButton } from 'components/common/Button';

const MarinaDetails = ({
  match,
  history,
  location,
  currentMarina,
  promotions
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const { sendToAmplitude } = useAmplitude();
  const {
    loading,
    setSubSectionTitle,
    setMetaDescription,
    setReviewSchema,
    marinas
  } = useContext(GlobalContext);
  const {
    loggedIn,
    user,
    statistics,
    selectedBoaterVessel,
    selectBoaterVessel
  } = useContext(AuthContext);
  const [, dispatch] = useContext(LayoutContext);

  const [isCarouselModalShowing, setIsCarouselModalShowing] = useState(false);
  const [associationType, setAssociationType] = useState(undefined);

  const [messageDrawerOpened, setMessageDrawerOpened] = useState(false);

  const [vesselsWithHomeMarina, setVesselsWithHomeMarina] = useState([]);
  const [isHomeMarina, setIsHomeMarina] = useState(false);

  const [selectedParentVessel, setSelectedParentVessel] = useState(null);
  const [sentAmplitudeEvent, setSentAmplitudeEvent] = useState(false);

  const [numberOfMarinasViewed, setNumberOfMarinasViewed] = useSessionStorage(
    'numberOfMarinasViewed',
    []
  );

  const marina = useMemo(
    () => ({
      ...currentMarina,
      author: 'Author',
      rating: 5,
      ratingCount: 35
    }),
    [currentMarina]
  );

  useEffect(() => {
    const searchParams = getStateFromUrl(location);
    if (searchParams && searchParams.open_messages) {
      setMessageDrawerOpened(true);
    }
  }, [match.params.id]);

  const updateNumberOfMarinasViewed = marinaId => {
    let viewedMarinas = numberOfMarinasViewed;
    if (marinaId && !viewedMarinas.includes(marinaId)) {
      viewedMarinas.push(marinaId);
      setNumberOfMarinasViewed(viewedMarinas);
    }
  };

  useEffect(() => {
    const searchParams = getStateFromUrl(location);
    if ((!searchParams || !searchParams.open_messages) && currentMarina) {
      setSubSectionTitle(currentMarina.name);
      setMetaDescription(
        `${currentMarina.name} - reservation, booking, berth, order, availability, chat, contact, berthing, instant  marina information: price, amenities, phone, country, city, location, hours, email, electricity, services, sail, sailing`
      );

      if (
        currentMarina.number_of_stars_rating &&
        currentMarina.number_of_rating
      ) {
        setReviewSchema({
          '@context': 'https://schema.org',
          '@type': 'Product',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: `${currentMarina.number_of_stars_rating}`,
            reviewCount: `${currentMarina.number_of_rating}`
          }
        });
      }

      //Amplitude event
      const boaterProperties = { boater: user, statistics };
      let origin = '';
      if (!location.state) {
        const searchParams = getStateFromUrl(location);
        if (searchParams.via) {
          origin = searchParams.via;
          history.replace({
            search: setStateToUrl({ ...searchParams, via: null })
          });
        } else if (searchParams.origin) {
          origin = searchParams.origin;
        } else origin = 'direct_link';
      } else if (location.state.ref) {
        origin = location.state.ref;
      }

      const eventProperties = {
        marina_id: currentMarina.id,
        marina_name: currentMarina.name,
        marina_country: currentMarina.country,
        association: currentMarina.association.name,
        origin
      };

      if (!sentAmplitudeEvent) {
        updateNumberOfMarinasViewed(currentMarina.id);
        sendToAmplitude({
          boaterProperties,
          eventProperties,
          eventName: 'marina_page_view'
        });
        setSentAmplitudeEvent(true);
      }
    }

    return () => {
      setMetaDescription(null);
      setSubSectionTitle(null);
    };
  }, [currentMarina, location, user, sentAmplitudeEvent]);

  useEffect(() => {
    if (
      currentMarina &&
      currentMarina.association &&
      currentMarina.association.code
    ) {
      setAssociationType(currentMarina.association.code);
    }
  }, [currentMarina]);

  useEffect(() => {
    if (
      currentMarina &&
      currentMarina.id &&
      user &&
      user.vessels &&
      width < 768 &&
      isMobile
    ) {
      const allVesselsWithHomeMarina = user && user.vessels && user.vessels.length
        ? user.vessels.filter(v => v.home_marina_id)
        : [];
      const currentMarinaIsHome = allVesselsWithHomeMarina.some(
        ({ home_marina_id }) => home_marina_id === currentMarina.id
      );
      setVesselsWithHomeMarina(allVesselsWithHomeMarina);
      setIsHomeMarina(currentMarinaIsHome);

      if (currentMarinaIsHome && selectedBoaterVessel) {
        const fullSelectedBoaterVessel = user.vessels.filter(
          ({ id }) => id === selectedBoaterVessel
        )[0];
        const vesselsVerifiedInThisMarina = allVesselsWithHomeMarina.filter(
          ({ home_marina_id }) => home_marina_id === currentMarina.id
        );
        if (fullSelectedBoaterVessel.home_marina_id) {
          if (fullSelectedBoaterVessel.home_marina_id === currentMarina.id) {
            // the selected vessel is verified in this marina
            setSelectedParentVessel(selectedBoaterVessel);
          } else {
            // the selected vessel is verified in another marina
            if (vesselsVerifiedInThisMarina.length) {
              selectBoaterVessel(vesselsVerifiedInThisMarina[0].id);
              setSelectedParentVessel(vesselsVerifiedInThisMarina[0].id);
            }
          }
        } else {
          // the selected vessel is not verified
          if (vesselsVerifiedInThisMarina.length) {
            selectBoaterVessel(vesselsVerifiedInThisMarina[0].id);
            setSelectedParentVessel(vesselsVerifiedInThisMarina[0].id);
          }
        }
      }
    }
  }, [currentMarina, user, width, isMobile, selectedBoaterVessel]);

  const onCloseMessageDrawer = () => {
    setMessageDrawerOpened(false);
    const searchParams = getStateFromUrl(location);
    if (searchParams && searchParams.open_messages) {
      history.push({
        pathname: location.pathname,
        state: { ref: 'message_to_marina' }
      });
    }
  };

  if (loading) return <Loader />;

  if (isEmpty(currentMarina)) {
    history.push(`/${match.params.locale}${NOT_FOUND_PAGE}`);
    return null;
  }

  let {
    id,
    images,
    city,
    country,
    geo_location,
    description,
    amenities,
    name,
    phone,
    country_code,
    hours,
    hplink,
    url
  } = marina;

  let marinaLocation;

  try {
    marinaLocation = {
      id,
      location: {
        lat: parseFloat(geo_location.lat),
        lng: parseFloat(geo_location.lng),
        formattedAddress: `${city}, ${country}`
      },
      title: name,
      image: { thumb_url: images && images.length ? images[0].thumb : null }
    };
  } catch (e) {
    marinaLocation = {
      id,
      location: {
        lat: 0,
        lng: 0,
        formattedAddress: `${city}, ${country}`
      },
      title: '',
      image: { thumb_url: null }
    };

    images = [{}];
  }

  const onClickMessage = () => {
    if (loggedIn) {
      setMessageDrawerOpened(true);
    } else {
      dispatch({ type: 'OPEN_AUTH_DRAWER' });
    }
  };

  return (
    <Fragment>
      {width < 768 ? (
        <Fragment>
          {isHomeMarina && (
            <VesselSelect
              className='home-marina-mobile-vessel-select'
              onlyHMRVessels={true}
              onSelectVessel={option => {
                // find the marina which is home for the selected vessel
                const selectedVessel = vesselsWithHomeMarina.filter(
                  ({ id }) => id === option
                );
                const textId = getMarinaTextId(
                  marinas.find(
                    ({ id }) => id === selectedVessel[0].home_marina_id
                  )
                );
                history.push(`/${match.params.locale}${MARINA_PAGE}/${textId}`);
                setSelectedParentVessel(option);
              }}
              selectedParentVessel={selectedParentVessel}
            />
          )}
          <CardCarousel mobileMode={true} height={186}>
            {images.slice(0, 5).map(({ thumb }, index) => (
              <ImageWithFallback
                key={index}
                draggable={false}
                src={thumb}
                alt={`${marina.name} pic ${index + 1}`}
              />
            ))}
          </CardCarousel>
          <MobileMainDescription>
            {Boolean(promotions && promotions.length) && (
              <PromotionsWrapperMobile>
                <PromotionsList
                  promotions={promotions}
                  handlePromotionMobileClick={() => {}}
                  handlePromotionDesktopClick={() => {}}
                />
              </PromotionsWrapperMobile>
            )}
            <Heading as='h1' content={name} className='marinaName' />
            <Text
              content={`${city}, ${t(country)}`}
              className='marinaLocation'
            />
            <Button
              className='message_button'
              htmlType='button'
              onClick={onClickMessage}
              type='primary'
            >
              {t('MESSAGE_BUTTON')}
            </Button>
          </MobileMainDescription>
          <Drawer
            className='messaging-drawer'
            placement='right'
            visible={messageDrawerOpened}
            onClose={onCloseMessageDrawer}
            width='767px'
            zIndex={999997}
            closable={false}
          >
            <MessagingHeader>
              <Button
                className='back_button'
                htmlType='button'
                onClick={onCloseMessageDrawer}
                type='link'
              >
                <FiArrowLeft />
                <BackButtonTitle>{name}</BackButtonTitle>
              </Button>
            </MessagingHeader>
            <Messages marina={marina} />
          </Drawer>
        </Fragment>
      ) : (
        <PostImage>
          <ImageWithFallback
            src={!isEmpty(images) && images[0].full}
            alt={`${name} pic`}
          />
          <Button
            type='primary'
            onClick={() => setIsCarouselModalShowing(true)}
            className='image_gallery_button'
          >
            {t('VIEW_PHOTOS')}
          </Button>
          <Modal
            visible={isCarouselModalShowing}
            onCancel={() => setIsCarouselModalShowing(false)}
            footer={null}
            width={`${width > 767 ? width - 60 : width - 40}px`}
            maskStyle={{ backgroundColor: '#ffffff' }}
            wrapClassName='image_gallery_modal'
            closable={false}
            zIndex={10001}
          >
            <Fragment>
              <PostImageGallery
                images={images.map(({ full: original, thumb: thumbnail }) => ({
                  original,
                  thumbnail
                }))}
              />
              <Button
                onClick={() => setIsCarouselModalShowing(false)}
                className='image_gallery_close'
              >
                <CloseCircleFilled style={{ color: '#194AB9' }} />
              </Button>
            </Fragment>
          </Modal>
        </PostImage>
      )}

      <TopBar marina={marina} />

      <Container fullWidth={window.location.hash === '#reservation'} noGutter={window.location.hash === '#reservation'}>
        {window.location.hash.includes('reservation')
            ?
            <Element name='reservation' className='Reservation'>
              <MarinaReservation
                  marina={marina}
              />
            </Element>
            :
            <Row
                gutter={30}
                id='reviewSection'
                style={width > 767 ? {marginTop: 30} : {marginTop: 23}}
            >
              <Col xs={24} lg={16}>
                <Description
                    associationType={associationType}
                    city={city}
                    country={country}
                    content={description}
                    title={name}
                    ratingCount={marina.number_of_rating}
                    rating={marina.number_of_stars_rating}
                />

                <Amenities amenities={amenities}/>

                <Location
                    city={city}
                    country={country}
                    location={marinaLocation}
                    phone={!isEmpty(country_code) ? country_code + phone : phone}
                    hours={hours}
                    hplink={url || hplink}
                />

                {/* <TermsAndPolicies /> */}
              </Col>
              <Col xs={24} lg={8}>
                {width > 991 ? (
                    <Sticky
                        innerZ={999}
                        activeClass='isSticky'
                        top={202}
                        bottomBoundary='#reviewSection'
                    >
                    <CustomButton
                      fillWidth
                      label={
                        t("BOOK_NOW")
                      }
                      onClick={e => {
                        window.location.hash = `#reservation`;
                          console.log("clicked")
                      }}
                    />
                    </Sticky>
                ) : (
                    <BottomReservation
                        marina={marina}
                        location={location}
                        history={history}
                        promotions={promotions}
                    />
                )}
              </Col>
            </Row>
        }
      </Container>
    </Fragment>
  );
};

const SinglePage = ({ match }) => {
  const { getSingleMarina } = useContext(GlobalContext);
  const { user } = useContext(AuthContext);
  const { getMarinaPromotions } = useContext(PopeyeContext);

  const currentMarina = getSingleMarina(match.params.id);

  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    if (user && currentMarina) {
      getPromotions(currentMarina.id);
    }
  }, [user, currentMarina]);

  const getPromotions = async id => {
    const res = await getMarinaPromotions(id);
    if (!res || (res.error_type && res.error_code)) {
      console.error(res?.error_code);
    } else {
      if (!res.length) return;
      // limit by max 4 displayed promotions and map
      const mapped = res.slice(0, 4).map(item => {
        const {
          promotion_type,
          points_multiplier,
          discount_percent,
          valid_between_start_date,
          valid_between_end_date
        } = item;
        let ret = {
          ...item,
          starts_discount_at: moment(valid_between_start_date),
          ends_discount_at: moment(valid_between_end_date)
        };
        if (promotion_type === 'POINTS_MULTIPLIER') {
          return {
            ...ret,
            multiple_by: parseFloat(points_multiplier).toFixed(1)
          };
        }
        if (promotion_type === 'DISCOUNT') {
          return { ...ret, discount_percent: parseFloat(discount_percent) };
        }
      });
      setPromotions(mapped);
    }
  };

  return (
    <SinglePageWrapper>
      <Route
        exact
        path={`${match.path}`}
        render={props => (
          <MarinaDetails
            currentMarina={currentMarina}
            promotions={promotions}
            {...props}
          />
        )}
      />
      <ProtectedRoute
        path={`${match.path}${MESSAGES_PAGE}`}
        render={props => <MessagingPage promotions={promotions} {...props} />}
      />
    </SinglePageWrapper>
  );
};

export default SinglePage;
